import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Block } from '@klp/design';
import {
    FilterCard,
    FilterDatePicker,
    FilterRow,
    FilterSelect,
    FilterSok,
    FilterStack,
} from '@klp/design/FilterPanel/FilterPanel';
import {
    getAktiveStatuser,
    getListParams,
} from '@klp/design/FilterPanel/listParams';
import { ContractToRegistrationTable } from '../../components';
import {
    contractToRegistrationSelectors,
    setFromDate,
    setMaxResult,
    setSelectedKunde,
    setStatus,
} from '../../store';

import { useGetContractToRegistrationListQuery } from '../../services/rtkApi';
import { ContractToRegistrationStatistics } from '../../components/ContractToRegistrationStatistics';
import RegistreringStatusSelector, {
    AllRegistrationStatuses,
} from '../../components/RegistreringStatusSelector';
import { useTranslation } from 'react-i18next';
import { usePortfolio, useRegion } from '@klp/portfolio/store';
import { PortfolioSelector, RegionSelector } from '@klp/portfolio/components';
import { contextSelectors } from '@klp/contextSelector';
import {
    parseSafeDate,
    formatDate,
    formatDateAPI,
} from '@klp/utils/dateTimeFormatter';
import { showToast } from '@klpkonsern/ui';
import { getDefaultFromDate } from '@forvalter/frontpage/hooks/useListFilters';
import { useRegistrationRoles } from '../../utils';

const ANTALL_VALG = [50, 100, 200];
const DEFAULT_ANTALL = 100;

export default function ContractRegistrationListView() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { fromDate, maxResult, selectedKunde } = useSelector(
        contractToRegistrationSelectors,
    );
    const { portfolio, resetPortfolioField } = usePortfolio();
    const { region, resetRegionField } = useRegion();
    const { selectedContext } = useSelector(contextSelectors);
    const [selectedRegistreringStatus, setSelectedRegistreringStatus] =
        useState<Array<string>>();
    const { registrationStatus: defaultStatusForRole } = useRegistrationRoles();

    // The text in the search field before the debounce, so the filters are
    // disabled and enabled again as soon as the user types
    const [sokTekst, setSokTekst] = useState(selectedKunde ?? '');
    const filtreLaast = !!sokTekst.trim();
    const sokPaagaar = !!selectedKunde?.trim();

    // null when the user has cleared the date field
    const fraDato =
        fromDate === null
            ? null
            : (parseSafeDate(fromDate) ?? getDefaultFromDate());
    // Stored values from before «Antall» was a fixed choice fall back to the default
    const antall = ANTALL_VALG.includes(maxResult) ? maxResult : DEFAULT_ANTALL;

    // «Alle» and empty fields are left out of the request, and a search is
    // sent without the filters. «Antall» limits the result, so it is always
    // sent.
    const contractListParameters = {
        ...getListParams(
            {
                forvalter: portfolio?.brukerId,
                region: region?.name,
                status: selectedRegistreringStatus
                    ? getAktiveStatuser(
                          selectedRegistreringStatus,
                          AllRegistrationStatuses,
                      )
                    : undefined,
                fromDate: fraDato ? formatDateAPI(fraDato) : undefined,
                byggNummer: selectedContext?.id,
            },
            { kunde: selectedKunde },
        ),
        pageSize: antall,
    };

    const {
        data: contractListData,
        isLoading: contractListLoading,
        isFetching: contractListFetching,
        isError: contractListError,
    } = useGetContractToRegistrationListQuery(contractListParameters, {
        // The status selector sets the initial statuses from storage or role
        skip: !sokPaagaar && !selectedRegistreringStatus,
    });

    const handleDateChange = (date: Date | null) => {
        dispatch(setFromDate(date ? formatDate(date) : null));
    };

    const handleNullstillFilters = () => {
        resetPortfolioField();
        resetRegionField();
        // The same statuses as the first time the list is opened
        dispatch(setStatus(defaultStatusForRole));
        dispatch(setFromDate(''));
        dispatch(setMaxResult(0));
        dispatch(setSelectedKunde(''));
    };

    if (contractListError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }

    const registrationLines = contractListData?.kontraktsDokuments ?? [];

    return (
        <>
            <Block paddingVertical="bulky">
                <ContractToRegistrationStatistics
                    statistics={contractListData?.statusGroupsCount}
                />
            </Block>

            <FilterCard
                loading={contractListLoading || contractListFetching}
                filters={
                    <FilterStack>
                        <FilterSok
                            id="kontrakt-sok"
                            label={t('contractRegistration.oppslag.sokLabel')}
                            value={selectedKunde}
                            onChange={(kunde) =>
                                dispatch(setSelectedKunde(kunde))
                            }
                            onInputChange={setSokTekst}
                            helperText={
                                filtreLaast
                                    ? t(
                                          'contractRegistration.oppslag.sokUtenFiltre',
                                      )
                                    : undefined
                            }
                        />
                        <FilterRow onReset={handleNullstillFilters}>
                            <PortfolioSelector
                                label="filterPanel.portefolje"
                                allLabel="filterPanel.alle"
                                filter={['isForvalter', 'isOkonomi']}
                                disabled={filtreLaast}
                            />

                            <RegionSelector
                                label="filterPanel.region"
                                allLabel="filterPanel.alle"
                                disabled={filtreLaast}
                            />

                            <RegistreringStatusSelector
                                handleApplyFilter={
                                    setSelectedRegistreringStatus
                                }
                                disabled={filtreLaast}
                            />

                            <FilterDatePicker
                                label={t('filterPanel.signertEtter')}
                                value={fraDato}
                                onChange={handleDateChange}
                                clearable={true}
                                disabled={filtreLaast}
                            />

                            <FilterSelect
                                id="kontrakt-antall"
                                label={t('filterPanel.antall')}
                                value={antall}
                                options={ANTALL_VALG.map((valg) => ({
                                    value: valg,
                                    label: String(valg),
                                }))}
                                onChange={(valg) =>
                                    dispatch(setMaxResult(valg))
                                }
                            />
                        </FilterRow>
                    </FilterStack>
                }
            >
                <ContractToRegistrationTable
                    registrationLines={registrationLines}
                />
            </FilterCard>
        </>
    );
}
